import { useEffect, useState } from "react";
import { Play, Smartphone, Download, Share2, Mail, Link2, Check } from "lucide-react";
import { useLanguage } from "@/i18n";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
export const demoFile = (base, lang, ext) => `/demo/${base}${lang === "nl" ? "-nl" : ""}${ext}`;

export const DemoChapters = ({ videoRef }) => {
  const { lang, t } = useLanguage();
  const [chapters, setChapters] = useState([]);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    fetch(demoFile("devisto-demo", lang, "-chapters.json")).then((r) => (r.ok ? r.json() : [])).then(setChapters).catch(() => setChapters([]));
  }, [lang]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !chapters.length) return;
    const onTime = () => {
      let i = -1;
      chapters.forEach((c, idx) => { if (v.currentTime >= c.start) i = idx; });
      setActive(i);
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [chapters, videoRef]);

  const jump = (c) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = c.start;
    v.muted = false;
    v.play().catch(() => {});
    v.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (!chapters.length) return null;
  return (
    <div data-testid="demo-chapters" className="mt-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C6A87]">{t("Aller directement à")}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {chapters.map((c, i) => (
          <button
            key={c.title}
            type="button"
            data-testid={`demo-chapter-${i}`}
            onClick={() => jump(c)}
            className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors duration-150 ${
              i === active ? "border-[#3A5CC8] bg-[#3A5CC8] text-white" : "border-[#E2E7F7] bg-white text-[#0F2645] hover:border-[#3A5CC8] hover:text-[#3A5CC8]"
            }`}
          >
            <Play className="h-3 w-3" /> {c.title}
            <span className={`font-mono text-[10px] ${i === active ? "text-white/80" : "text-[#93A0BC]"}`}>{fmt(c.start)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const DemoVertical = () => {
  const { lang, t } = useLanguage();
  return (
    <section data-testid="demo-vertical" className="mt-14 grid gap-8 lg:grid-cols-[1fr_1.4fr] lg:items-center">
      <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[2rem] border-[6px] border-[#08104D] bg-[#08104D] shadow-[0_30px_70px_-30px_rgba(8,16,77,0.7)]">
        <video key={lang} data-testid="demo-vertical-video" controls playsInline preload="metadata" poster={demoFile("devisto-demo-vertical", lang, "-poster.jpg")} className="aspect-[9/16] w-full bg-black">
          <source src={demoFile("devisto-demo-vertical", lang, ".mp4")} type="video/mp4" />
        </video>
      </div>
      <div>
        <span className="inline-flex items-center gap-2 rounded-full border border-[#3A5CC8]/30 bg-[#EEF1FC] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]"><Smartphone className="h-3.5 w-3.5" /> {t("Version verticale · Reels & TikTok")}</span>
        <h2 className="mt-4 font-heading text-base font-bold text-[#0F2645] md:text-lg">{t("La même histoire en 9:16, sur téléphone : dossier dicté, devis IA, PDF, signature sur place.")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-[#5C6A87]">{t("Format prêt à publier sur Instagram Reels, TikTok, YouTube Shorts et Facebook. Voix off et sous-titres inclus — regardable sans le son.")}</p>
        <a href={demoFile("devisto-demo-vertical", lang, ".mp4")} download data-testid="demo-vertical-download" className="mt-5 inline-flex items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-5 py-3 text-sm font-semibold text-[#0F2645] hover:border-[#3A5CC8]"><Download className="h-4 w-4" /> {t("Télécharger la version verticale")}</a>
      </div>
    </section>
  );
};

export const DemoShare = () => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/video-demo`;
  const text = t("Regardez comment Devisto fait signer le devis chez le client, avant de partir (démo 6 min) :");
  const wa = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
  const mail = `mailto:?subject=${encodeURIComponent(t("Démo Devisto Studio — le devis signé sur place"))}&body=${encodeURIComponent(`${text}\n${url}`)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div data-testid="demo-share" className="mt-6 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#5C6A87]"><Share2 className="h-3.5 w-3.5" /> {t("Partager la démo")}</span>
      <a href={wa} target="_blank" rel="noreferrer" data-testid="demo-share-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-bold text-white transition-transform duration-150 hover:-translate-y-0.5">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z"/></svg>
        WhatsApp
      </a>
      <a href={mail} data-testid="demo-share-email" className="inline-flex items-center gap-2 rounded-full border border-[#E2E7F7] bg-white px-4 py-2 text-xs font-bold text-[#0F2645] transition-colors duration-150 hover:border-[#3A5CC8]"><Mail className="h-3.5 w-3.5" /> {t("E-mail")}</a>
      <button type="button" onClick={copy} data-testid="demo-share-copy" className="inline-flex items-center gap-2 rounded-full border border-[#E2E7F7] bg-white px-4 py-2 text-xs font-bold text-[#0F2645] transition-colors duration-150 hover:border-[#3A5CC8]">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Link2 className="h-3.5 w-3.5" />} {copied ? t("Lien copié") : t("Copier le lien")}
      </button>
    </div>
  );
};
